/**
 * The ONE progress surface of the intake (`feedback_progress_indication_minimal`).
 *
 * DI-3-S48-FE (story §2.14, Christian 2026-10-23) — a PILL, not a bar: small,
 * fixed, bottom centre, never in flow (DL-02's download pill,
 * `apps/lyse/src/features/image-download/DownloadStatusBar.tsx`, is the
 * model): a spinner and one line — "Uploading originals · 212 of 960 ·
 * 3.1 MB/s · about 18 min". The bottom edge of the page is free again, so the
 * page's bottom box ("Before you go" + Start on Upload, "You can leave" +
 * Approve all green on My intake) sits really at the bottom. When DL-02's
 * pill runs too, both sit side by side in the shell's pill dock
 * (`app/chrome/PillDock.tsx`) — never on top of each other. A modal covers
 * the pill (it sits under the dialogs' layer): a full-screen dialog keeps no
 * clearance for it any more (the 40 px `STATUS_BAR_CLEARANCE_PX` is gone).
 *
 *   index    "Reading images · indexing 212 / 527 · hashing 40 / 527" (Upload)
 *   sync     DI-2-4 — "Sending metadata · 500 of 527"
 *   upload   DI-3-F — the upload's totals over the sets that send now; the
 *            board mounts it with `uploadOnly` (the index line belongs to the
 *            Upload page). DI-3-S68 F14 — the drop's AND the upload lane's
 *            sets (every upload this tab drives); paused = the shared queue's
 *   done     DI-3-P6 — "Drop done · n data sets verified" for a few seconds
 *            after the session ended by itself (`runner/sessionEnd.ts`)
 */
import { useEffect, useState, type ReactElement } from "react";
import { createPortal } from "react-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import { compactControlHeight, radius, spacing } from "@aeriosiq/ui";
import { useIntakeIndexStore } from "../indexStore.js";
import { useSyncProgress } from "../sync/syncProgressStore.js";
import { intakeRunner, type IntakeRunner } from "../runner/intakeRunner.js";
import { useRunnerState, type RunnerSet } from "../runner/runnerStore.js";
import { uploadStartable } from "../runner/uploadPending.js";
import { formatAbout, formatBytesPair, formatMbit } from "../board/eta.js";
import { useSessionEndState } from "../runner/sessionEnd.js";
import { uploadLane, type UploadLane } from "../runner/uploadLane.js";
import { useLaneUploadSets, useQueuePaused } from "../upload/tabUploads.js";

/**
 * The shell's dock both bottom pills render into (`app/chrome/PillDock.tsx`):
 * a fixed row at the bottom centre — the intake pill and the download pill
 * side by side. Without it (a test, another host) the pill places itself.
 */
export const PILL_DOCK_ID = "aiq-bottom-pill-dock";

export function statusText(
  phase: string,
  total: number,
  parsed: number,
  hashed: number,
): string {
  const parts = [
    `indexing ${String(parsed)} / ${String(total)}`,
    `hashing ${String(hashed)} / ${String(total)}`,
  ];
  // DI-3-S68 F5 — files left unread: the reading is not "done"
  if (phase === "done" && hashed < total)
    return `${parts.join(" · ")} · ${String(total - hashed)} unreadable`;
  if (phase === "done") return `done · ${parts.join(" · ")}`;
  if (phase === "cancelled") return `stopped · ${parts.join(" · ")}`;
  return parts.join(" · ");
}

/** DI-3-F — the upload totals over the sets that send now; null when none does. */
export function uploadTotals(sets: readonly RunnerSet[]): {
  sent: number;
  total: number;
  filesDone: number;
  filesTotal: number;
} | null {
  let sent = 0;
  let total = 0;
  let filesDone = 0;
  let filesTotal = 0;
  let any = false;
  for (const s of sets) {
    const u = s.upload;
    if (!u || !uploadStartable(s)) continue;
    if (u.phase !== "uploading" && u.phase !== "committing") continue;
    any = true;
    sent += u.bytesSent;
    total += u.bytesTotal > 0 ? u.bytesTotal : s.sizeBytes;
    filesDone += u.filesDone;
    filesTotal += u.filesTotal > 0 ? u.filesTotal : s.imageCount;
  }
  return any ? { sent, total, filesDone, filesTotal } : null;
}

/** "upload 1.9 / 6.3 GB at 18 Mbit/s" (the rate once the uplink showed one). */
export function uploadText(
  totals: { sent: number; total: number },
  bytesPerS: number | null,
  paused: boolean,
): string {
  const head = `upload ${formatBytesPair(totals.sent, totals.total)}`;
  if (paused) return `${head} · paused`;
  return bytesPerS !== null && bytesPerS > 0
    ? `${head} at ${formatMbit(bytesPerS)}`
    : head;
}

/** DI-3-S68 F12 — the rate's place while the sliding window fills. */
export const MEASURING = "measuring…";

/** "3.1 MB/s" — the pill's rate (bytes per second in). */
export function formatMBps(bytesPerS: number): string {
  const mb = bytesPerS / 1e6;
  return `${mb >= 10 ? mb.toFixed(0) : mb.toFixed(1)} MB/s`;
}

/**
 * DI-3-S48-FE board 1 — the pill's upload line: the bold head and its muted
 * rest ("212 of 960 · 3.1 MB/s · about 18 min"; paused: "212 of 960").
 */
export function uploadPillText(
  totals: {
    sent: number;
    total: number;
    filesDone: number;
    filesTotal: number;
  },
  bytesPerS: number | null,
  paused: boolean,
): { head: string; rest: string } {
  const count = `${String(totals.filesDone)} of ${String(totals.filesTotal)}`;
  if (paused) return { head: "Uploads paused", rest: count };
  const parts = [count];
  if (bytesPerS !== null && bytesPerS > 0) {
    parts.push(formatMBps(bytesPerS));
    const left = Math.max(0, totals.total - totals.sent);
    if (left > 0) parts.push(formatAbout(left / bytesPerS));
  } else {
    // DI-3-S68 F12 — the window holds no rate yet: no number, no time left
    parts.push(MEASURING);
  }
  return { head: "Uploading originals", rest: parts.join(" · ") };
}

/** The dock the shell mounts, once it is in the DOM (null until then / without one). */
function usePillDock(): HTMLElement | null {
  const [dock, setDock] = useState<HTMLElement | null>(() =>
    typeof document === "undefined"
      ? null
      : document.getElementById(PILL_DOCK_ID),
  );
  useEffect(() => {
    if (!dock) setDock(document.getElementById(PILL_DOCK_ID));
  }, [dock]);
  return dock;
}

export function IntakeStatusBar({
  uploadOnly = false,
  runner = intakeRunner,
  lane = uploadLane,
}: {
  uploadOnly?: boolean;
  runner?: IntakeRunner;
  /** DI-3-S68 F14 — the upload lane of this tab (null: none — tests). */
  lane?: UploadLane | null;
} = {}): ReactElement | null {
  const phase = useIntakeIndexStore((s) => s.phase);
  const total = useIntakeIndexStore((s) => s.total);
  const parsed = useIntakeIndexStore((s) => s.parsedCount);
  const hashed = useIntakeIndexStore((s) => s.hashedCount);
  const syncing = useSyncProgress((s) => s.active);
  const syncSent = useSyncProgress((s) => s.sent);
  const syncTotal = useSyncProgress((s) => s.total);
  const runnerSets = useRunnerState(runner.state, (s) => s.sets);
  // DI-3-S68 F14 — every upload this tab drives: the drop's AND the lane's
  const laneSets = useLaneUploadSets(lane);
  const sets = laneSets.length > 0 ? [...runnerSets, ...laneSets] : runnerSets;
  // the shared queue's pause holds both (never a flag a new drop resets)
  const paused = useQueuePaused(runner.uploads.queue);
  const dock = usePillDock();
  const up = uploadTotals(sets);
  const upLine = up
    ? uploadPillText(up, runner.uploads.queue.rate(), paused)
    : null;
  const doneLine = useSessionEndState((s) => s.line);
  const indexShown =
    !uploadOnly && (syncing || (phase !== "idle" && total > 0));
  if (!indexShown && !upLine && !doneLine) return null;
  const running = indexShown
    ? syncing || phase === "indexing" || phase === "hashing"
    : Boolean(up) && !paused;
  const line: { head: string; rest: string } = indexShown
    ? syncing
      ? {
          head: "Sending metadata",
          rest: `${String(syncSent)} of ${String(syncTotal)}`,
        }
      : {
          head: "Reading images",
          rest: statusText(phase, total, parsed, hashed),
        }
    : (upLine ?? { head: doneLine ?? "", rest: "" });
  const pill = (
    <Box
      role="status"
      aria-live="polite"
      data-testid="intake-status-bar"
      data-phase={
        indexShown
          ? syncing
            ? "syncing"
            : phase
          : upLine
            ? "uploading"
            : "ended"
      }
      data-docked={dock ? "true" : "false"}
      sx={{
        // in the shell's dock: in its row; without one: fixed itself
        ...(dock
          ? { order: -1 }
          : {
              position: "fixed",
              left: "50%",
              transform: "translateX(-50%)",
              bottom: `${String(spacing["4"])}px`,
              zIndex: (t) => t.zIndex.appBar - 1,
            }),
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        pl: 1,
        pr: 1.5,
        height: `${String(compactControlHeight)}px`,
        borderRadius: `${String(radius.pill)}px`,
        bgcolor: "background.default",
        border: 1,
        borderColor: "divider",
        boxShadow: (t) => t.shadows[1],
        maxWidth: `calc(100vw - ${String(spacing["8"])}px)`,
        pointerEvents: "auto",
      }}
    >
      <Box
        sx={{
          width: `${String(spacing["5"])}px`,
          height: `${String(spacing["5"])}px`,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flex: "none",
        }}
      >
        {running && (
          <CircularProgress
            // board 1 — a spinner on a quiet track (the line carries the numbers)
            size={14}
            thickness={6}
            sx={{
              borderRadius: `${String(radius.pill)}px`,
              boxShadow: (t) => `inset 0 0 0 2px ${t.palette.divider}`,
            }}
            aria-label={
              indexShown
                ? syncing
                  ? "Sync progress"
                  : "Indexing progress"
                : "Upload progress"
            }
          />
        )}
      </Box>
      <Typography
        variant="caption"
        noWrap
        sx={{ fontVariantNumeric: "tabular-nums" }}
        data-testid="intake-status-bar-line"
      >
        <Box component="strong" sx={{ fontWeight: 600 }}>
          {line.head}
        </Box>
        {line.rest !== "" && (
          <Box component="span" sx={{ color: "text.secondary" }}>
            {` · ${line.rest}`}
          </Box>
        )}
      </Typography>
    </Box>
  );
  return dock ? createPortal(pill, dock) : pill;
}
